import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import api from "../../../api";
import CategorySaveFields from "./CategorySaveFields";

const DIFFICULTIES = ["Mixed", "Easy", "Medium", "Hard"];

/**
 * Generates a similar exam paper from an import: kicks off the async job, polls
 * it, then hands off to the Similar Paper review page. Modelled on
 * ConvertToMcqDialog — nothing reaches the question bank until the admin
 * approves questions in review.
 *
 * Props:
 *   open, onClose
 *   importId          — question bank import id
 *   title             — paper name, shown in the dialog
 *   questionCount     — how many questions the source paper has; also the default count
 *   defaultCategoryId — pre-selects the import's target category
 */
const GenerateSimilarPaperDialog = ({
  open,
  onClose,
  importId,
  title,
  questionCount,
  defaultCategoryId = "",
}) => {
  const navigate = useNavigate();
  const pollRef = useRef(null);

  const [count, setCount] = useState(questionCount || 10);
  const [difficulty, setDifficulty] = useState("Mixed");
  const [shuffleQuestions, setShuffleQuestions] = useState(false);
  const [shuffleOptions, setShuffleOptions] = useState(false);
  const [topicHint, setTopicHint] = useState("");
  const [categoryId, setCategoryId] = useState(defaultCategoryId || "");
  const [sourceType, setSourceType] = useState("ai_generated");

  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    setCategoryId(defaultCategoryId || "");
  }, [defaultCategoryId, open]);

  useEffect(() => {
    if (open && questionCount) setCount(questionCount);
  }, [questionCount, open]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const stopPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const startPolling = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(
          `/admin/question-bank/import/${importId}/similar-paper-status`,
          { params: { job_id: jobId } }
        );
        const job = res.data?.data;
        setProgress(job?.progress_percent || 0);

        if (job?.status === "completed") {
          stopPolling();
          setGenerating(false);
          onClose?.();
          navigate(`/admin/question-bank/similar-papers?job=${jobId}`);
        } else if (job?.status === "failed") {
          stopPolling();
          setGenerating(false);
          setError(job?.error_message || "Generation failed.");
        }
      } catch (err) {
        stopPolling();
        setGenerating(false);
        setError(err.response?.data?.message || "Failed to check generation status.");
      }
    }, 3000);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError("");
    setProgress(0);

    try {
      const res = await api.post(
        `/admin/question-bank/import/${importId}/generate-similar-paper`,
        {
          count,
          difficulty,
          source: "ai",
          shuffle: shuffleQuestions,
          shuffle_options: shuffleOptions,
          topic_hint: topicHint.trim() || undefined,
          target_source_type: sourceType,
          target_category_id: categoryId || null,
          async: true,
        }
      );

      const jobId = res.data?.data?.similar_paper_job_id;
      if (!jobId) throw new Error("No job id returned");
      startPolling(jobId);
    } catch (err) {
      setGenerating(false);
      setError(err.response?.data?.message || "Failed to start similar paper generation.");
    }
  };

  const handleClose = () => {
    if (generating) return;
    setError("");
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesomeIcon color="primary" />
        Generate Similar Paper
      </DialogTitle>

      <DialogContent dividers>
        {title && (
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            {title}
            {questionCount ? (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {questionCount} question{questionCount === 1 ? "" : "s"}
              </Typography>
            ) : null}
          </Typography>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          AI writes a new paper mirroring this one&rsquo;s structure and question types, with
          fresh values and regenerated diagrams. Nothing is saved to the question bank until
          you review and approve the questions.
        </Alert>

        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            type="number"
            label="Question count"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
            disabled={generating}
            inputProps={{ min: 1, max: 100 }}
            sx={{ width: 150 }}
          />
          <TextField
            select
            size="small"
            label="Difficulty"
            value={difficulty}
            onChange={(e) => setDifficulty(e.target.value)}
            disabled={generating}
            sx={{ minWidth: 140 }}
          >
            {DIFFICULTIES.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Topic focus (optional)"
            placeholder="e.g. Fractions, Ratio"
            value={topicHint}
            onChange={(e) => setTopicHint(e.target.value)}
            disabled={generating}
            sx={{ flex: "1 1 180px" }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
          <FormControlLabel
            control={
              <Switch
                checked={shuffleQuestions}
                onChange={(e) => setShuffleQuestions(e.target.checked)}
                disabled={generating}
              />
            }
            label={<Typography variant="body2">Shuffle question order</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={shuffleOptions}
                onChange={(e) => setShuffleOptions(e.target.checked)}
                disabled={generating}
              />
            }
            label={<Typography variant="body2">Shuffle MCQ option order</Typography>}
          />
        </Box>

        <CategorySaveFields
          categoryId={categoryId}
          onCategoryChange={setCategoryId}
          sourceType={sourceType}
          onSourceTypeChange={setSourceType}
          disabled={generating}
          helperText="You can change these again before saving to the question bank."
        />

        {generating && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Generating similar paper&hellip; {progress}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={generating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={generating}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
        >
          {generating ? "Generating…" : "Generate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateSimilarPaperDialog;
